import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createMembre, getMembres, createPaiement, getCotisationsByMembre, getMontantMensuel, getMontantsMensuels, getStatsCotisations, getStatsRecolteur, getPaiements, getEnfantsMembre, createEnfantMembre, updateEnfantMembre, deleteEnfantMembre } from '../services/api';
import WebcamCapture from '../components/WebcamCapture';
import FicheAdhesion from '../components/FicheAdhesion';
import DetailMembre from '../components/DetailMembre';
import Footer from '../components/Footer';
import InstallPrompt from '../components/InstallPrompt';
import NotificationBell from '../components/NotificationBell';
import { notificationService } from '../utils/notifications';
import { initDB, saveMembreOffline, savePaiementOffline, getPendingSyncCount } from '../services/offlineStorage';
import { setupConnectionListener, syncAll, manualSync, startAutoSync, isOnline } from '../services/syncService';
import './DashboardRecolteur.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const DashboardRecolteur = () => {
  const navigate = useNavigate();
  // Mémoriser l'objet admin pour éviter les re-renders inutiles
  const admin = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('recolteur_user') || '{}');
    } catch {
      return {};
    }
  }, []);
  const [showForm, setShowForm] = useState(false);
  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [selectedMembreFiche, setSelectedMembreFiche] = useState(null);
  const [selectedMembreDetail, setSelectedMembreDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [, setMontantsMensuels] = useState([]);
  const [, setStatsCotisations] = useState([]);
  const [, setLoadingMontants] = useState(false);
  // États pour les cotisations (utilisés par loadCotisationsImpayees)
  const [, setCotisationsImpayees] = useState([]);
  const [montantHebdomadaire, setMontantHebdomadaire] = useState(null);
  const [, setMontantMensuel] = useState(null);
  const [, setLoadingCotisations] = useState(false);
  const [statsRecolteur, setStatsRecolteur] = useState({
    globales: { totalMembres: 0, totalMontantRecolte: 0 },
    journalieres: { membresAujourdhui: 0, montantRecolteAujourdhui: 0 }
  });
  const [loadingStats, setLoadingStats] = useState(true); // Commencer à true pour afficher le chargement initial
  const [paiementsCarte, setPaiementsCarte] = useState([]); // Paiements de carte de membre (type 'autre' avec montant 5000)
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine); // État de connexion
  const [pendingSyncCount, setPendingSyncCount] = useState(0); // Nombre d'éléments en attente de synchronisation
  
  // État du formulaire
  const [formData, setFormData] = useState({
    nom: '',
    postnom: '',
    prenom: '',
    date_naissance: '',
    lieu_naissance: '',
    quartier: '',
    commune: '',
    telephone: '',
    profession: '',
    association: admin.association || '', // Pré-remplir avec l'association du récolteur
    centre_sante: '', // Laisser vide - sera assigné par l'administrateur
    nom_epouse: '',
    nombre_enfants: '0',
    noms_enfants: '',
    date_adhesion: new Date().toISOString().split('T')[0],
    nom_recolteur: admin.nom_complet || ''
  });

  const [files, setFiles] = useState({
    photo: null,
    photo_signature: null
  });

  // États pour gérer les enfants des membres
  const [enfantsMembre, setEnfantsMembre] = useState([]);
  const [nouvelEnfant, setNouvelEnfant] = useState({ nom: '', prenom: '', date_naissance: '', lien: '' });
  const [membrePourEnfants, setMembrePourEnfants] = useState(null);
  const [photoEnfant, setPhotoEnfant] = useState(null);
  const [webcamKeyEnfant, setWebcamKeyEnfant] = useState(0);
  const [showEnfantsModal, setShowEnfantsModal] = useState(false);

  const loadMembres = React.useCallback(async () => {
    try {
      const response = await getMembres();
      const allMembres = response.data || [];
      
      // Filtrer uniquement les membres du récolteur connecté
      // Utiliser recolteur_id si disponible, sinon nom_recolteur comme fallback
      const membresDuRecolteur = allMembres.filter(membre => {
        if (admin.role === 'admin') {
          // Les admins voient tous les membres
          return true;
        }
        // Pour les récolteurs, filtrer par recolteur_id ou nom_recolteur
        if (membre.recolteur_id && admin.id) {
          return membre.recolteur_id === admin.id;
        }
        // Fallback: utiliser nom_recolteur si recolteur_id n'est pas défini
        if (membre.nom_recolteur && admin.nom_complet) {
          return membre.nom_recolteur === admin.nom_complet;
        }
        // Si aucun identifiant, ne pas afficher le membre
        return false;
      });
      
      setMembres(membresDuRecolteur);
      console.log(`✅ Membres chargés pour le récolteur ${admin.nom_complet}: ${membresDuRecolteur.length} membre(s)`);
    } catch (err) {
      console.error('Erreur lors du chargement des membres:', err);
      setMembres([]);
    }
  }, [admin]);

  const loadPaiementsCarte = async () => {
    try {
      const response = await getPaiements();
      // Filtrer uniquement les paiements de type 'autre' avec montant = 5000
      const paiementsCarteFiltres = (response.data || []).filter(p => 
        p.type_paiement === 'autre' && parseFloat(p.montant) === 5000
      );
      setPaiementsCarte(paiementsCarteFiltres);
      console.log('💳 Paiements de carte chargés:', paiementsCarteFiltres.length);
    } catch (err) {
      console.error('Erreur lors du chargement des paiements de carte:', err);
      setPaiementsCarte([]);
    }
  };

  // Fonction pour vérifier si un membre a déjà payé la carte
  const membreACartePayee = (membreId) => {
    return paiementsCarte.some(p => p.membre_id === membreId);
  };

  const loadStatsRecolteur = async () => {
    try {
      setLoadingStats(true);
      
      // Timeout de 10 secondes pour éviter un chargement infini
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 10000)
      );
      
      const response = await Promise.race([
        getStatsRecolteur(),
        timeoutPromise
      ]);
      
      console.log('Réponse statistiques récolteur:', response.data);
      if (response && response.data) {
        setStatsRecolteur(response.data);
      } else {
        setStatsRecolteur({
          globales: { totalMembres: 0, totalMontantRecolte: 0 },
          journalieres: { membresAujourdhui: 0, montantRecolteAujourdhui: 0 }
        });
      }
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
      console.error('Détails de l\'erreur:', err.response?.data || err.message);
      // Afficher les valeurs par défaut même en cas d'erreur
      setStatsRecolteur({
        globales: { totalMembres: 0, totalMontantRecolte: 0 },
        journalieres: { membresAujourdhui: 0, montantRecolteAujourdhui: 0 }
      });
    } finally {
      setLoadingStats(false);
    }
  };

  // Initialiser le mode hors ligne
  useEffect(() => {
    const initOfflineMode = async () => {
      try {
        await initDB();
        console.log('✅ Mode hors ligne initialisé');
        
        // Configurer les écouteurs de connexion
        setupConnectionListener((online) => {
          setIsOnlineState(online);
          if (online) {
            // Synchroniser automatiquement quand la connexion revient
            syncAll();
          }
        });
        
        // Démarrer la synchronisation automatique
        startAutoSync(30000); // Toutes les 30 secondes
        
        // Mettre à jour le compteur d'éléments en attente
        const updatePendingCount = async () => {
          const count = await getPendingSyncCount();
          setPendingSyncCount(count);
        };
        updatePendingCount();
        setInterval(updatePendingCount, 10000); // Mettre à jour toutes les 10 secondes
      } catch (error) {
        console.error('❌ Erreur lors de l\'initialisation du mode hors ligne:', error);
      }
    };
    
    initOfflineMode();
    
    // Nettoyer à la déconnexion
    return () => {
      // La synchronisation continue en arrière-plan
    };
  }, []);

  useEffect(() => {
    if (!admin.id) {
      navigate('/login-recolteur');
      return;
    }
    if (admin.role !== 'recolteur' && admin.role !== 'admin') {
      navigate('/login-recolteur');
      return;
    }
    
    // Charger les données de manière séquentielle pour éviter la surcharge
    const loadData = async () => {
      await loadMembres();
      // Charger seulement les montants, pas les stats (trop lourd au chargement)
      // Les stats seront chargées à la demande si nécessaire
      try {
        const montantsRes = await getMontantsMensuels();
        setMontantsMensuels(montantsRes.data || []);
      } catch (err) {
        console.error('Erreur lors du chargement des montants:', err);
        setMontantsMensuels([]);
      }
      
      // Charger les paiements de carte de membre
      loadPaiementsCarte();
      
      // Charger les statistiques du récolteur
      loadStatsRecolteur();
    };
    
    loadData();
  }, [admin.id, admin.role, navigate, loadMembres]);


  // Mettre à jour l'association et le centre de santé dans le formulaire au montage initial
  useEffect(() => {
    if (admin.association || admin.centre_sante) {
      setFormData(prev => ({
        ...prev,
        association: prev.association || admin.association || '',
        centre_sante: '' // Laisser vide - sera assigné par l'administrateur
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Seulement au montage initial

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileCapture = (file, fieldName) => {
    setFiles(prev => ({
      ...prev,
      [fieldName]: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Convertir les fichiers en URLs blob pour le stockage local
      let photoBlobUrl = null;
      let signatureBlobUrl = null;
      
      if (files.photo) {
        photoBlobUrl = URL.createObjectURL(files.photo);
      }
      if (files.photo_signature) {
        signatureBlobUrl = URL.createObjectURL(files.photo_signature);
      }

      // Préparer les données du membre
      const membreData = {
        ...formData,
        photo: photoBlobUrl,
        photo_signature: signatureBlobUrl
      };

      // Vérifier si on est en ligne
      if (!isOnline()) {
        // Mode hors ligne : sauvegarder localement
        console.log('📴 Mode hors ligne - Sauvegarde locale');
        const result = await saveMembreOffline(membreData);
        
        // Mettre à jour le compteur
        const count = await getPendingSyncCount();
        setPendingSyncCount(count);
        
        setSuccess(`✅ Membre enregistré en mode hors ligne!\n\nMatricule temporaire: ${result.id}\n\nLes données seront synchronisées automatiquement lorsque la connexion sera rétablie.\n\nÉléments en attente: ${count}`);
        notificationService.success('Enregistré hors ligne', 'Le membre sera synchronisé automatiquement quand la connexion reviendra');
        
        // Réinitialiser le formulaire
        setFormData({
          nom: '',
          postnom: '',
          prenom: '',
          date_naissance: '',
          lieu_naissance: '',
          quartier: '',
          commune: '',
          telephone: '',
          profession: '',
          association: admin.association || '',
          centre_sante: '',
          nom_epouse: '',
          nombre_enfants: '0',
          noms_enfants: '',
          date_adhesion: new Date().toISOString().split('T')[0],
          nom_recolteur: admin.nom_complet || ''
        });
        setFiles({ photo: null, photo_signature: null });
        setShowForm(false);
        setLoading(false);
        return;
      }

      // Mode en ligne : enregistrer normalement
      const data = new FormData();
      
      // Ajouter tous les champs du formulaire
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '') {
          data.append(key, formData[key]);
        }
      });

      // Ajouter les fichiers
      if (files.photo) {
        data.append('photo', files.photo);
      }
      if (files.photo_signature) {
        data.append('photo_signature', files.photo_signature);
      }

      const response = await createMembre(data);
      
      // Notification d'enregistrement
      notificationService.membreEnregistre(response.data.membre);
      
      // Créer automatiquement le paiement de la carte de membre
      const nouveauMembre = response.data.membre;
      const montantCarte = 5000;
      
      try {
        console.log('💳 Création automatique du paiement de carte de membre:', {
          membre_id: nouveauMembre.id,
          montant: montantCarte,
          type_paiement: 'autre'
        });

        const paiementData = {
          membre_id: nouveauMembre.id,
          montant: montantCarte,
          date_paiement: new Date().toISOString().split('T')[0],
          type_paiement: 'autre'
        };

        const paiementResponse = await createPaiement(paiementData);
        
        console.log('✅ Paiement de carte de membre créé automatiquement:', paiementResponse.data);
        
        const numeroRecu = paiementResponse.data?.paiement?.numero_recu || 'N/A';
        setSuccess(`Membre créé avec succès! Matricule: ${nouveauMembre.numero_matricule}, Mot de passe: ${nouveauMembre.password}\n\nPaiement de la carte (${montantCarte.toLocaleString('fr-FR')} CDF) enregistré. Numéro de reçu: ${numeroRecu}`);
      } catch (paiementErr) {
        console.error('❌ Erreur lors de la création automatique du paiement de carte:', paiementErr);
        // Ne pas bloquer l'enregistrement du membre si le paiement échoue
        setSuccess(`Membre créé avec succès! Matricule: ${nouveauMembre.numero_matricule}, Mot de passe: ${nouveauMembre.password}\n\n⚠️ Le paiement de la carte n'a pas pu être enregistré automatiquement. Vous pouvez l'enregistrer manuellement.`);
        notificationService.error('Avertissement', 'Le membre a été créé mais le paiement de la carte n\'a pas pu être enregistré automatiquement.');
      }
      
      // Attendre un peu pour que le fichier soit complètement écrit
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Recharger la liste des membres pour afficher la photo
      await loadMembres();
      
      // Réinitialiser le formulaire (garder l'association et le centre de santé du récolteur)
      setFormData({
        nom: '',
        postnom: '',
        prenom: '',
        date_naissance: '',
        lieu_naissance: '',
        quartier: '',
        commune: '',
        telephone: '',
        profession: '',
        association: admin.association || '', // Garder l'association du récolteur
        centre_sante: '', // Laisser vide - sera assigné par l'administrateur
        nom_epouse: '',
        nombre_enfants: '0',
        noms_enfants: '',
        date_adhesion: new Date().toISOString().split('T')[0],
        nom_recolteur: admin.nom_complet || ''
      });
      setFiles({ photo: null, photo_signature: null });
      setShowForm(false);
      
      // Recharger la liste et les statistiques
      loadMembres();
      loadPaiementsCarte();
      loadStatsRecolteur();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création du membre');
    } finally {
      setLoading(false);
    }
  };

  const handlePaiement = (membreId) => {
    // Rediriger vers la nouvelle page de confirmation des paiements
    navigate(`/recolteur/paiement/${membreId}`);
  };

  // Fonctions pour gérer les enfants des membres
  const loadEnfantsMembre = async (membreId) => {
    try {
      const response = await getEnfantsMembre(membreId);
      const enfants = response?.data || response || [];
      setEnfantsMembre(Array.isArray(enfants) ? enfants : []);
    } catch (error) {
      console.error('Erreur lors du chargement des enfants:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors du chargement des enfants');
      setEnfantsMembre([]);
    }
  };

  const handleAddEnfantsMembre = async (membre) => {
    setMembrePourEnfants(membre);
    await loadEnfantsMembre(membre.id);
    setShowEnfantsModal(true);
  };

  const handlePhotoEnfantCapture = (file, fieldName) => {
    if (fieldName === 'photo') {
      setPhotoEnfant(file);
      console.log('📸 Photo de l\'enfant capturée:', file);
    }
  };

  const handleAddEnfantMembreSubmit = async () => {
    if (!nouvelEnfant.nom || !nouvelEnfant.prenom) {
      notificationService.warning('Attention', 'Veuillez remplir au moins le nom et le prénom');
      return;
    }

    if (!membrePourEnfants) {
      notificationService.error('Erreur', 'Aucun membre sélectionné');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('nom', nouvelEnfant.nom);
      formData.append('prenom', nouvelEnfant.prenom);
      if (nouvelEnfant.date_naissance) {
        formData.append('date_naissance', nouvelEnfant.date_naissance);
      }
      if (nouvelEnfant.lien) {
        formData.append('lien', nouvelEnfant.lien);
      }
      if (photoEnfant) {
        formData.append('photo', photoEnfant);
      }

      await createEnfantMembre(membrePourEnfants.id, formData);
      notificationService.success('Succès', 'Enfant ajouté avec succès');
      
      // Réinitialiser le formulaire
      setNouvelEnfant({ nom: '', prenom: '', date_naissance: '', lien: '' });
      setPhotoEnfant(null);
      setWebcamKeyEnfant(prev => prev + 1);
      
      // Recharger les enfants
      await loadEnfantsMembre(membrePourEnfants.id);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'enfant:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'ajout de l\'enfant');
    }
  };

  const handleDeleteEnfantMembre = async (enfantId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet enfant ?')) {
      return;
    }

    if (!membrePourEnfants) {
      notificationService.error('Erreur', 'Aucun membre sélectionné');
      return;
    }

    try {
      await deleteEnfantMembre(membrePourEnfants.id, enfantId);
      notificationService.success('Succès', 'Enfant supprimé avec succès');
      
      // Recharger les enfants
      await loadEnfantsMembre(membrePourEnfants.id);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'enfant:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression de l\'enfant');
    }
  };

  const handlePaiementCarteMembre = async (membreId) => {
    const membre = membres.find(m => m.id === membreId);
    const membreNom = membre ? `${membre.nom} ${membre.prenom}` : 'ce membre';
    const montantCarte = 5000;
    
    const messageConfirmation = `Confirmer le paiement de la carte de membre pour ${membreNom} (${membre?.numero_matricule}) ?\n\nMontant: ${montantCarte.toLocaleString('fr-FR')} CDF`;
    
    if (!window.confirm(messageConfirmation)) {
      return;
    }

    // Définir paiementData avant le try pour qu'il soit accessible dans le catch
    const paiementData = {
      membre_id: membreId,
      montant: montantCarte,
      date_paiement: new Date().toISOString().split('T')[0],
      type_paiement: 'autre'
      // semaine n'est pas nécessaire pour les paiements de type "autre"
    };

    try {
      console.log('💳 Création du paiement de carte de membre:', {
        membre_id: membreId,
        montant: montantCarte,
        type_paiement: 'autre'
      });

      // Vérifier si on est en ligne
      if (!isOnline()) {
        // Mode hors ligne : sauvegarder localement
        console.log('📴 Mode hors ligne - Sauvegarde locale du paiement');
        const result = await savePaiementOffline(paiementData);
        
        // Mettre à jour le compteur
        const count = await getPendingSyncCount();
        setPendingSyncCount(count);
        
        notificationService.success(
          'Paiement enregistré hors ligne', 
          `Le paiement de la carte de membre (${montantCarte.toLocaleString('fr-FR')} CDF) pour ${membreNom} a été enregistré localement. Il sera synchronisé automatiquement lorsque la connexion sera rétablie.\n\nÉléments en attente: ${count}`
        );
        
        // Recharger les données (même si hors ligne, on peut recharger depuis le cache)
        await loadMembres();
        await loadPaiementsCarte();
        await loadStatsRecolteur();
        return;
      }

      // Mode en ligne : enregistrer normalement
      const paiementResponse = await createPaiement(paiementData);
      
      console.log('✅ Paiement de carte de membre créé avec succès:', paiementResponse.data);
      
      // Notification de paiement
      const numeroRecu = paiementResponse.data?.paiement?.numero_recu || 'N/A';
      notificationService.success(
        'Paiement confirmé', 
        `Le paiement de la carte de membre (${montantCarte.toLocaleString('fr-FR')} CDF) pour ${membreNom} a été enregistré avec succès. Numéro de reçu: ${numeroRecu}`
      );
      
      // Recharger les données
      await loadMembres();
      await loadPaiementsCarte();
      await loadStatsRecolteur();
    } catch (err) {
      // Si l'erreur est due à une absence de connexion, essayer de sauvegarder hors ligne
      if (!isOnline() || err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        try {
          console.log('📴 Erreur réseau - Tentative de sauvegarde hors ligne');
          const result = await savePaiementOffline(paiementData);
          const count = await getPendingSyncCount();
          setPendingSyncCount(count);
          
          notificationService.success(
            'Paiement enregistré hors ligne', 
            `Le paiement a été enregistré localement et sera synchronisé automatiquement. Éléments en attente: ${count}`
          );
          return;
        } catch (offlineError) {
          console.error('❌ Erreur lors de la sauvegarde hors ligne:', offlineError);
        }
      }
      
      console.error('❌ Erreur lors de la création du paiement de carte de membre:', err);
      console.error('❌ Détails:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de l\'enregistrement du paiement de la carte de membre';
      notificationService.error('Erreur de paiement', errorMessage);
      alert(`Erreur: ${errorMessage}`);
    }
  };

  const loadCotisationsImpayees = async (membreId) => {
    setLoadingCotisations(true);
    try {
      const now = new Date();
      const mois = now.getMonth() + 1;
      const annee = now.getFullYear();

      // Récupérer le montant mensuel et hebdomadaire actuel
      try {
        const montantRes = await getMontantMensuel(mois, annee);
        setMontantMensuel(parseFloat(montantRes.data.montant_mensuel));
        setMontantHebdomadaire(parseFloat(montantRes.data.montant_hebdomadaire));
      } catch (err) {
        console.warn('Aucun montant mensuel fixé pour ce mois:', err);
        setMontantMensuel(null);
        setMontantHebdomadaire(null);
      }

      // Récupérer toutes les cotisations du membre
      const cotisationsRes = await getCotisationsByMembre(membreId);
      const cotisations = cotisationsRes.data;
      
      // Créer un tableau avec les 4 semaines du mois en cours
      const semainesCompletes = [];
      for (let semaine = 1; semaine <= 4; semaine++) {
        // Chercher la cotisation correspondante
        const cotisationExistante = cotisations.find(c => 
          c.mois === mois && 
          c.annee === annee && 
          c.semaine === semaine
        );
        
        if (cotisationExistante) {
          // Utiliser la cotisation existante
          semainesCompletes.push(cotisationExistante);
        } else {
          // Créer une cotisation virtuelle pour cette semaine
          const jourEcheance = Math.min(semaine * 7, 28);
          const dateEcheance = new Date(annee, mois - 1, jourEcheance);
          
          semainesCompletes.push({
            id: null, // Pas encore créée
            membre_id: membreId,
            mois: mois,
            annee: annee,
            semaine: semaine,
            montant: montantHebdomadaire || 0,
            statut: 'impaye',
            date_echeance: dateEcheance.toISOString().split('T')[0],
            date_paiement: null,
            paiement_id: null,
            created_at: null,
            _virtuelle: true // Marqueur pour indiquer que c'est une cotisation virtuelle
          });
        }
      }
      
      // Trier par semaine
      semainesCompletes.sort((a, b) => a.semaine - b.semaine);
      
      // Filtrer les impayées (y compris les virtuelles)
      const impayees = semainesCompletes.filter(c => c.statut === 'impaye');
      setCotisationsImpayees(impayees);
    } catch (err) {
      console.error('Erreur lors du chargement des cotisations:', err);
      alert('Erreur lors du chargement des cotisations');
    } finally {
      setLoadingCotisations(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('recolteur_token');
    localStorage.removeItem('recolteur_user');
    navigate('/login-recolteur');
  };


  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img src="/logo.png" alt="Logo" style={{ height: '50px', width: '50px', objectFit: 'contain' }} />
            <div>
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <p>Bienvenue, {admin.nom_complet}</p>
            </div>
          </div>
          <div className="header-actions">
            {/* Indicateur de statut de connexion */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              marginRight: '10px',
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor: isOnlineState ? '#d4edda' : '#f8d7da',
              color: isOnlineState ? '#155724' : '#721c24',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {isOnlineState ? (
                <>
                  <span>🌐 En ligne</span>
                  {pendingSyncCount > 0 && (
                    <span style={{ 
                      backgroundColor: '#ffc107', 
                      color: '#000', 
                      padding: '2px 8px', 
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {pendingSyncCount} en attente
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span>📴 Hors ligne</span>
                  {pendingSyncCount > 0 && (
                    <span style={{ 
                      backgroundColor: '#ffc107', 
                      color: '#000', 
                      padding: '2px 8px', 
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {pendingSyncCount} en attente
                    </span>
                  )}
                </>
              )}
            </div>
            
            {/* Bouton de synchronisation manuelle */}
            {pendingSyncCount > 0 && isOnlineState && (
              <button 
                className="btn btn-primary" 
                onClick={async () => {
                  notificationService.info('Synchronisation', 'Synchronisation en cours...');
                  await manualSync();
                  const count = await getPendingSyncCount();
                  setPendingSyncCount(count);
                }}
                style={{ marginRight: '10px' }}
              >
                🔄 Synchroniser ({pendingSyncCount})
              </button>
            )}
            
            {/* Cloche de notifications */}
            <NotificationBell />
            
            <button className="btn btn-secondary" onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Annuler' : 'Nouveau Membre'}
            </button>
            <button className="btn btn-danger" onClick={handleLogout}>
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Section Statistiques */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* Statistiques Journalières */}
            <div className="card" style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              border: 'none',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
            }}>
              <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                📅 Statistiques Journalières
              </h3>
              {loadingStats ? (
                <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>
                  Chargement...
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '5px' }}>
                      Membres enregistrés aujourd'hui
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'white' }}>
                      {statsRecolteur?.journalieres?.membresAujourdhui ?? 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '5px' }}>
                      Montant récolté aujourd'hui
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'white' }}>
                      {parseFloat(statsRecolteur?.journalieres?.montantRecolteAujourdhui ?? 0).toLocaleString('fr-FR', { 
                        minimumFractionDigits: 0, 
                        maximumFractionDigits: 0 
                      })} CDF
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Statistiques Globales */}
            <div className="card" style={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
              color: 'white',
              border: 'none',
              boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)'
            }}>
              <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                📊 Statistiques Globales
              </h3>
              {loadingStats ? (
                <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>
                  Chargement...
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '5px' }}>
                      Total membres enregistrés
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'white' }}>
                      {statsRecolteur?.globales?.totalMembres ?? 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '5px' }}>
                      Montant total récolté
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'white' }}>
                      {parseFloat(statsRecolteur?.globales?.totalMontantRecolte ?? 0).toLocaleString('fr-FR', { 
                        minimumFractionDigits: 0, 
                        maximumFractionDigits: 0 
                      })} CDF
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {showForm && (
          <div className="card">
            <h2>Enregistrer un nouveau membre</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Postnom *</label>
                  <input
                    type="text"
                    name="postnom"
                    value={formData.postnom}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Prénom *</label>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date de naissance *</label>
                  <input
                    type="date"
                    name="date_naissance"
                    value={formData.date_naissance}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Lieu de naissance *</label>
                  <input
                    type="text"
                    name="lieu_naissance"
                    value={formData.lieu_naissance}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Date d'adhésion *</label>
                  <input
                    type="date"
                    name="date_adhesion"
                    value={formData.date_adhesion}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Avenue, numéro, Quartier *</label>
                  <input
                    type="text"
                    name="quartier"
                    value={formData.quartier}
                    onChange={handleInputChange}
                    placeholder="Avenue, numéro, Quartier"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Commune *</label>
                  <select
                    name="commune"
                    value={formData.commune}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sélectionner une commune</option>
                    <option value="MAKISO">MAKISO</option>
                    <option value="KABONDO">KABONDO</option>
                    <option value="MANGOBO">MANGOBO</option>
                    <option value="LUBUNGA">LUBUNGA</option>
                    <option value="KISANGANI">KISANGANI</option>
                    <option value="TSHOPO">TSHOPO</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Téléphone *</label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Profession</label>
                  <input
                    type="text"
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Association</label>
                  <input
                    type="text"
                    name="association"
                    value={formData.association}
                    readOnly
                    style={{ 
                      backgroundColor: '#f0f0f0',
                      cursor: 'not-allowed'
                    }}
                    placeholder="Association"
                  />
                </div>

                {/* Le centre de santé sera assigné par l'administrateur - champ masqué */}

                {/* Champ "Nom de l'épouse ou épouse" masqué */}
                {/* <div className="form-group">
                  <label>Nom de l'épouse ou épouse</label>
                  <input
                    type="text"
                    name="nom_epouse"
                    value={formData.nom_epouse}
                    onChange={handleInputChange}
                  />
                </div> */}
              </div>

              {/* Champs "Nombre d'enfants" et "Noms des enfants" masqués */}
              {/* <div className="form-row">
                <div className="form-group">
                  <label>Nombre d'enfants (-16 ans)</label>
                  <input
                    type="number"
                    name="nombre_enfants"
                    value={formData.nombre_enfants}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Noms des enfants (séparés par des virgules)</label>
                  <input
                    type="text"
                    name="noms_enfants"
                    value={formData.noms_enfants}
                    onChange={handleInputChange}
                    placeholder="Enfant 1, Enfant 2, ..."
                  />
                </div>
              </div> */}

              <div className="form-group">
                <WebcamCapture
                  onCapture={handleFileCapture}
                  label="Photo du membre"
                  fieldName="photo"
                  photoType="passeport"
                />
              </div>

              <div className="form-group">
                <WebcamCapture
                  onCapture={handleFileCapture}
                  label="Photo de la signature"
                  fieldName="photo_signature"
                  photoType="signature"
                />
              </div>

              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : 'Enregistrer le membre'}
              </button>
            </form>
          </div>
        )}

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
            <h2 style={{ flex: '1 1 100%', marginBottom: '10px' }}>Liste des membres ({membres.length})</h2>
            <div style={{ position: 'relative', width: '100%', maxWidth: '300px', minWidth: '200px' }}>
              <input
                type="text"
                placeholder="🔍 Rechercher un membre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 40px 10px 15px',
                  border: '2px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#999'
                  }}
                  title="Effacer la recherche"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          {(() => {
            const filteredMembres = membres.filter(membre => {
              if (!searchTerm) return true;
              const search = searchTerm.toLowerCase();
              return (
                (membre.nom && membre.nom.toLowerCase().includes(search)) ||
                (membre.postnom && membre.postnom.toLowerCase().includes(search)) ||
                (membre.prenom && membre.prenom.toLowerCase().includes(search)) ||
                (membre.numero_matricule && membre.numero_matricule.toLowerCase().includes(search)) ||
                (membre.telephone && membre.telephone.includes(search)) ||
                (membre.association && membre.association.toLowerCase().includes(search)) ||
                (membre.quartier && membre.quartier.toLowerCase().includes(search)) ||
                (membre.commune && membre.commune.toLowerCase().includes(search))
              );
            });
            return (
              <>
                {searchTerm && (
                  <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                    {filteredMembres.length} membre(s) trouvé(s)
                  </div>
                )}
                <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="table">
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>Photo</th>
                <th style={{ whiteSpace: 'nowrap' }}>Matricule</th>
                <th style={{ whiteSpace: 'nowrap' }}>Nom complet</th>
                <th style={{ whiteSpace: 'nowrap' }}>Date adhésion</th>
                <th style={{ whiteSpace: 'nowrap' }}>Téléphone</th>
                <th style={{ whiteSpace: 'nowrap' }}>Centre de santé</th>
                <th style={{ whiteSpace: 'nowrap' }}>Nombre d'enfants</th>
                <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembres.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    {searchTerm ? 'Aucun membre trouvé' : 'Aucun membre enregistré'}
                  </td>
                </tr>
              ) : (
                filteredMembres.map(membre => (
                <tr key={membre.id}>
                  <td data-label="Photo">
                    {membre.photo ? (
                      <img 
                        src={`${API_BASE_URL}${membre.photo}`} 
                        alt={`${membre.nom} ${membre.prenom}`}
                        style={{ 
                          width: '60px', 
                          height: '60px', 
                          objectFit: 'cover', 
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                        onClick={() => window.open(`${API_BASE_URL}${membre.photo}`, '_blank')}
                        title="Cliquez pour agrandir"
                        onError={(e) => {
                          console.error('Erreur de chargement de la photo:', `${API_BASE_URL}${membre.photo}`);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    {!membre.photo && (
                      <span style={{ color: '#999', fontSize: '12px' }}>Aucune photo</span>
                    )}
                    <span style={{ display: 'none', color: '#999', fontSize: '12px' }}>Erreur de chargement</span>
                  </td>
                  <td data-label="Matricule">{membre.numero_matricule}</td>
                  <td data-label="Nom complet">{membre.nom} {membre.postnom || ''} {membre.prenom}</td>
                  <td data-label="Date adhésion">{membre.date_adhesion}</td>
                  <td data-label="Téléphone">{membre.telephone || '-'}</td>
                  <td data-label="Centre de santé">{membre.centre_sante || '-'}</td>
                  <td data-label="Nombre d'enfants" style={{ textAlign: 'center' }}>
                    {membre.enfants ? membre.enfants.length : 0}
                  </td>
                  <td data-label="Actions" style={{ whiteSpace: 'nowrap' }}>
                    <select
                      onChange={(e) => {
                        const action = e.target.value;
                        if (action === 'details') {
                          setSelectedMembreDetail(membre);
                        } else if (action === 'paiement') {
                          handlePaiement(membre.id);
                        } else if (action === 'carte') {
                          handlePaiementCarteMembre(membre.id);
                        } else if (action === 'ajouter_enfants') {
                          handleAddEnfantsMembre(membre);
                        }
                        // Réinitialiser la sélection
                        e.target.value = '';
                      }}
                      style={{
                        padding: '8px 12px',
                        fontSize: '13px',
                        borderRadius: '5px',
                        border: '1px solid #2c3e50',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        minWidth: '200px',
                        fontWeight: '500',
                        color: '#2c3e50'
                      }}
                      defaultValue=""
                      title="Sélectionner une action"
                    >
                      <option value="" disabled hidden>⚙️ Actions...</option>
                      <option value="details">👁️ Voir Détails</option>
                      <option value="paiement">💰 Payer Cotisations</option>
                      {!membreACartePayee(membre.id) && (
                        <option value="carte">🎫 Payer Carte (5000 CDF)</option>
                      )}
                      {membreACartePayee(membre.id) && (
                        <option value="" disabled style={{ color: '#28a745', fontStyle: 'italic' }}>
                          ✅ Carte déjà payée
                        </option>
                      )}
                      <option value="ajouter_enfants">👶 Ajouter les enfants</option>
                    </select>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {selectedMembreFiche && (
        <FicheAdhesion 
          membre={selectedMembreFiche} 
          onClose={() => setSelectedMembreFiche(null)} 
        />
      )}

      {selectedMembreDetail && (
        <DetailMembre 
          membre={selectedMembreDetail} 
          onClose={() => setSelectedMembreDetail(null)} 
        />
      )}

      {/* Modal pour gérer les enfants du membre */}
      {showEnfantsModal && membrePourEnfants && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#2c3e50' }}>
                👶 Enfants de {membrePourEnfants.nom} {membrePourEnfants.prenom} ({enfantsMembre.length})
              </h2>
              <button
                onClick={() => {
                  setShowEnfantsModal(false);
                  setMembrePourEnfants(null);
                  setEnfantsMembre([]);
                  setNouvelEnfant({ nom: '', prenom: '', date_naissance: '', lien: '' });
                  setPhotoEnfant(null);
                  setWebcamKeyEnfant(prev => prev + 1);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ✕
              </button>
            </div>

            {/* Formulaire pour ajouter un enfant */}
            <div style={{ marginBottom: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '5px', border: '1px solid #ddd' }}>
              <h3 style={{ marginBottom: '15px', color: '#2c3e50', fontSize: '16px' }}>Ajouter un enfant</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Nom *</label>
                  <input
                    type="text"
                    value={nouvelEnfant.nom}
                    onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, nom: e.target.value })}
                    placeholder="Nom"
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Prénom *</label>
                  <input
                    type="text"
                    value={nouvelEnfant.prenom}
                    onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, prenom: e.target.value })}
                    placeholder="Prénom"
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Date de naissance</label>
                  <input
                    type="date"
                    value={nouvelEnfant.date_naissance}
                    onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, date_naissance: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Lien</label>
                  <select
                    value={nouvelEnfant.lien}
                    onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, lien: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="">Sélectionner</option>
                    <option value="Épouse">Épouse</option>
                    <option value="Fils">Fils</option>
                    <option value="Fille">Fille</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <WebcamCapture
                  key={`enfant-photo-${webcamKeyEnfant}`}
                  onCapture={handlePhotoEnfantCapture}
                  label="Photo de l'enfant"
                  fieldName="photo"
                  photoType="passeport"
                />
              </div>
              <button
                type="button"
                onClick={handleAddEnfantMembreSubmit}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2c3e50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                ➕ Ajouter
              </button>
            </div>

            {/* Liste des enfants existants */}
            {enfantsMembre.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Photo</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nom</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Prénom</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date de naissance</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Lien</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date d'enregistrement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enfantsMembre.map((enfant) => (
                      <tr key={enfant.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px' }}>
                          {enfant.photo ? (
                            <img 
                              src={`${API_BASE_URL}${enfant.photo}`} 
                              alt={`${enfant.nom} ${enfant.prenom}`}
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <span style={{ color: '#999', fontSize: '12px' }}>Pas de photo</span>
                          )}
                        </td>
                        <td style={{ padding: '10px' }}>{enfant.nom}</td>
                        <td style={{ padding: '10px' }}>{enfant.prenom}</td>
                        <td style={{ padding: '10px' }}>{enfant.date_naissance || '-'}</td>
                        <td style={{ padding: '10px' }}>{enfant.lien || '-'}</td>
                        <td style={{ padding: '10px' }}>{enfant.created_at ? new Date(enfant.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontStyle: 'italic', background: '#f8f9fa', borderRadius: '5px', border: '1px solid #ddd' }}>
                Aucun enfant enregistré pour ce membre
              </div>
            )}
          </div>
        </div>
      )}

      <InstallPrompt />
      <Footer />
    </div>
  );
};

export default DashboardRecolteur;

